import React, {useEffect, useState} from 'react';
import {VerticalNavigationBar} from '@forecast-it/design-system';
import {Link, useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useSecondaryNavState} from '../navigation/secondary/SecondaryNavigationStateManager';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../constants';

// Mirrors the NavigationLink helper in SecondaryNavigation.js so VerticalNavigationBar.Link gets the
// same delayed-navigation behaviour (lets dropdowns close before navigating). Duplicated here (2nd use)
// per KISS — extract a shared helper on the 3rd consumer.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NavigationLink = React.forwardRef<HTMLAnchorElement, any>(({navigate, target, onClick, ...props}, ref) => {
	const isModifiedEvent = (event: React.MouseEvent) => !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

	const handleClick = (event: React.MouseEvent) => {
		try {
			if (onClick) onClick(event);
		} catch (ex) {
			event.preventDefault();
			throw ex;
		}

		if (!event.defaultPrevented && event.button === 0 && (!target || target === '_self') && !isModifiedEvent(event)) {
			event.preventDefault();
			// Timeout to make sure the dropdown has time to close
			setTimeout(() => navigate(), 50);
		}
	};

	return (
		<VerticalNavigationBar.Link ref={ref} {...props} onClick={handleClick}>
			{props.children}
		</VerticalNavigationBar.Link>
	);
});

type Props = {
	clientId: string;
	currentPath: string;
	updateCurrentPath: (path: string) => void;
};

const ClientSecondaryNavigation: React.FC<Props> = ({clientId, currentPath, updateCurrentPath}) => {
	const {formatMessage} = useIntl();
	const {expanded, locked, toggleLock, open, close} = useSecondaryNavState();
	const [menuHeight, setMenuHeight] = useState<number | undefined>(undefined);
	const history = useHistory();

	useEffect(() => {
		const updateMenuHeight = () => {
			const topNav = document.querySelector('[class^="TopNavigation"]') as HTMLElement | null;
			if (topNav) {
				setMenuHeight(window.innerHeight - topNav.offsetHeight);
			}
		};
		window.addEventListener('resize', updateMenuHeight);
		updateMenuHeight();
		return () => window.removeEventListener('resize', updateMenuHeight);
	}, []);

	useEffect(() => {
		const unlisten = history.listen(location => {
			updateCurrentPath(location.pathname + location.hash);
		});
		return () => unlisten();
	}, [history, updateCurrentPath]);

	const overviewPath = `/clients/${clientId}`;
	// Overview is active whenever we're on the client base path (no sub-section yet).
	const isOverviewActive = currentPath.replace(/[#?].*$/, '') === overviewPath;

	// The Opportunities tab ships dark behind the feature flag + module; hidden until both are enabled.
	const showOpportunities = hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) && hasModule(MODULE_TYPES.OPPORTUNITIES);
	const opportunitiesPath = `/clients/${clientId}/opportunities`;
	const isOpportunitiesActive = currentPath.replace(/[#?].*$/, '') === opportunitiesPath;

	return (
		<VerticalNavigationBar
			expanded={expanded}
			locked={locked}
			onToggle={toggleLock}
			open={open}
			close={close}
			maxHeight={menuHeight}
			defaultValue={''}
		>
			<VerticalNavigationBar.Item isActive={isOverviewActive}>
				<Link to={overviewPath} component={NavigationLink} data-cy="secondary-nav-client-overview">
					{formatMessage({id: 'company_view_page.nav_overview'})}
				</Link>
			</VerticalNavigationBar.Item>
			{showOpportunities && (
				<VerticalNavigationBar.Item isActive={isOpportunitiesActive}>
					<Link to={opportunitiesPath} component={NavigationLink} data-cy="secondary-nav-client-opportunities">
						{formatMessage({id: 'company_view_page.nav_opportunities'})}
					</Link>
				</VerticalNavigationBar.Item>
			)}
		</VerticalNavigationBar>
	);
};

export default ClientSecondaryNavigation;
